import { type HTMLAttributes } from 'react'
import clsx from 'clsx'
import { Tag } from '@digdir/designsystemet-react'
import { Icon, type IconName } from './Icon'

// ---------------------------------------------------------------------------
// Badge
//
// Presentational status pill. Props are domain-named; styling is delegated
// to Designsystemet's Tag. Legacy classes remain layout hooks.
//
// API (contracts/component-library.md):
//   tone   — default | success | warn | danger | info | accent
//   dot    — prepend a 6px circular indicator (currentColor)
//   solid  — filled primary style (.badge--solid-primary); takes precedence
//             over tone-derived modifier
//   children — the badge label (already i18n'd by the caller)
// ---------------------------------------------------------------------------

export type BadgeTone = 'default' | 'success' | 'warn' | 'danger' | 'info' | 'accent'

export type BadgeProps = {
  /** Light outlined status label, rather than a filled pill. */
  outline?: boolean
  icon?: IconName
  /** Semantic tone controlling background + foreground color. */
  tone?: BadgeTone
  /** Render a small circular dot indicator before the label. */
  dot?: boolean
  /**
   * Filled solid-primary style. When true, .badge--solid-primary is applied
   * regardless of tone.
   */
  solid?: boolean
  children?: React.ReactNode
} & Omit<HTMLAttributes<HTMLSpanElement>, 'role'>

/**
 * Status pill / tag. Presentational — no data fetching, no auth logic.
 * Safe for the public sessionless register bundle.
 *
 * Usage:
 *   <Badge tone="success">Innvilget</Badge>
 *   <Badge tone="warn" dot>Til behandling</Badge>
 *   <Badge solid>Primær</Badge>
 */
export function Badge({
  tone = 'default',
  dot = false,
  solid = false,
  outline = false,
  icon,
  children,
  className,
  ...rest
}: BadgeProps) {
  const modifierClass = solid
    ? 'badge--solid-primary'
    : tone !== 'default'
      ? `badge--${tone}`
      : undefined

  return (
    <Tag
      data-color={solid ? undefined : tone === 'default' ? 'neutral' : tone === 'warn' ? 'warning' : tone === 'accent' ? 'gold' : tone}
      variant={outline && !solid ? 'outline' : 'default'}
      data-size="sm"
      role="status"
      className={clsx('badge', modifierClass, outline && !solid && 'badge--outline', className)}
      {...rest}
    >
      {dot && <span className="badge__dot" aria-hidden="true" />}
      {icon && <Icon name={icon} size={14} aria-hidden="true" />}
      {children}
    </Tag>
  )
}
